// ESTÁNDARES UNIFICADOS PARA EMPRESAS Y SOLICITUDES
// Archivo: src/utils/empresaStandards.js

// ========================================
// ESTADOS ESTÁNDAR UNIFICADOS
// ========================================

export const ESTADOS_EMPRESA = {
  // ESTADOS INICIALES (Catastro masivo)
  CATALOGADA: 'catalogada',           // Empresa del catastro inicial (200+ empresas)
  INGRESADA: 'ingresada',             // Empresa ingresada manualmente por admin
  PENDIENTE_VALIDACION: 'pendiente_validacion', // Asignada a agente para validación
  CORREO_ENVIADO: 'correo_enviado',   // Correo de bienvenida/visita enviado, pendiente visita (SLA)
  
  // ESTADOS DE VALIDACIÓN
  EN_VISITA: 'en_visita',             // Agente programó visita
  VALIDADA: 'validada',               // Agente validó en terreno
  RECHAZADA: 'rechazada',             // No cumple requisitos
  
  // ESTADOS OPERATIVOS
  ACTIVA: 'activa',                   // Visible en home y operativa
  SUSPENDIDA: 'suspendida',           // Temporalmente desactivada
  INACTIVA: 'inactiva'                // Permanentemente desactivada
};

export const ESTADOS_SOLICITUD = {
  INGRESADA: 'ingresada',             // Empresa envió solicitud (onboarding KawesCar)
  PENDIENTE: 'pendiente',             // Legacy: solicitud nueva de proveedor
  ASIGNADA: 'asignada',               // Admin asignó agente
  EN_REVISION: 'en_revision',         // Legacy: bajo revisión administrativa
  EN_VISITA: 'en_visita',             // Agente programó visita (7 días SLA)
  APROBADA: 'aprobada',               // Legacy: aprobada
  ACTIVA: 'activa',                   // Agente activó tras visita
  RECHAZADA: 'rechazada'              // Rechazada administrativamente
};

// Estado público del perfil (visible en directorio/Home). Independiente del onboarding.
export const PUBLIC_STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE'
};

// Motivos de desactivación (ocultar del directorio)
export const DEACTIVATION_REASONS = {
  WEB_PENDING: 'WEB_PENDING',
  BAD_RATINGS: 'BAD_RATINGS',
  NO_LOGO: 'NO_LOGO',
  OTHER: 'OTHER'
};

export const DEACTIVATION_REASON_LABELS = {
  [DEACTIVATION_REASONS.WEB_PENDING]: 'Pendiente página web / cambios',
  [DEACTIVATION_REASONS.BAD_RATINGS]: 'Malas calificaciones',
  [DEACTIVATION_REASONS.NO_LOGO]: 'Falta de logo',
  [DEACTIVATION_REASONS.OTHER]: 'Otro'
};

/** Transiciones válidas onboarding empresas KawesCar. */
export const TRANSICIONES_ONBOARDING = {
  ingresada: ['asignada'],
  pendiente: ['asignada', 'en_revision'],
  asignada: ['en_visita'],
  en_revision: ['en_visita', 'aprobada', 'activa'],
  en_visita: ['activa', 'aprobada'],
};

// Flujo definitivo onboarding (4 estados) - solo Admin cambia INGRESADA→ASIGNADA; agente asignado ASIGNADA→EN_REVISION→ACTIVADA
export const ONBOARDING_STATUS = {
  INGRESADA: 'ingresada',
  ASIGNADA: 'asignada',
  EN_REVISION: 'en_revision',
  ACTIVADA: 'activada'
};

// Claves de timestamps para medición de tiempos entre estados (solicitud/empresa)
export const ONBOARDING_TIMESTAMPS = {
  createdAt: 'createdAt',
  assignedAt: 'assignedAt',
  reviewStartedAt: 'reviewStartedAt',
  activatedAt: 'activatedAt'
};

/** Normaliza estado de solicitud/empresa al flujo definitivo de 4 etapas. */
export function normalizeOnboardingStatus(estado) {
  if (!estado) return ONBOARDING_STATUS.INGRESADA;
  const e = String(estado).toLowerCase().replace(/\s/g, '_');
  if (e === 'asignada') return ONBOARDING_STATUS.ASIGNADA;
  if (e === 'en_revision' || e === 'en_visita') return ONBOARDING_STATUS.EN_REVISION;
  if (e === 'activa' || e === 'activada' || e === 'aprobada' || e === 'validada') return ONBOARDING_STATUS.ACTIVADA;
  return ONBOARDING_STATUS.INGRESADA;
}

/** Devuelve fechas de cada etapa para mostrar tiempos entre estados. doc = datos de solicitud/empresa. */
export function getOnboardingTimestamps(doc) {
  const toDate = (v) => {
    if (!v) return null;
    if (v && typeof v.toDate === 'function') return v.toDate();
    if (v && v.seconds) return new Date(v.seconds * 1000);
    if (typeof v === 'number') return new Date(v);
    if (v instanceof Date) return v;
    return null;
  };
  return {
    createdAt: toDate(doc?.createdAt || doc?.fecha_creacion),
    assignedAt: toDate(doc?.assignedAt || doc?.fecha_asignacion),
    reviewStartedAt: toDate(doc?.reviewStartedAt),
    activatedAt: toDate(doc?.activatedAt || doc?.fecha_activacion),
  };
}

// ========================================
// FLUJO DE VIDA COMPLETO DE EMPRESA
// ========================================

export const FLUJO_EMPRESA = {
  // FLUJO 1: CATÁSTRO INICIAL (Admin)
  CATALOGADA: {
    descripcion: 'Empresa del catastro inicial (200+ empresas)',
    visible_en_home: false,
    puede_gestionar: false,
    proximo_paso: 'Asignar a agente para validación',
    transiciones: ['pendiente_validacion']
  },
  
  // FLUJO 1.5: EMPRESAS INGRESADAS MANUALMENTE (Admin)
  INGRESADA: {
    descripcion: 'Empresa ingresada manualmente por administrador',
    visible_en_home: false,
    puede_gestionar: false,
    proximo_paso: 'Admin puede activar directamente o asignar a agente',
    transiciones: ['activa', 'pendiente_validacion', 'rechazada']
  },
  
  // FLUJO 2: VALIDACIÓN POR AGENTE
  PENDIENTE_VALIDACION: {
    descripcion: 'Asignada a agente para validación en terreno',
    visible_en_home: false,
    puede_gestionar: false,
    proximo_paso: 'Enviar correo de bienvenida y aviso de visita',
    transiciones: ['correo_enviado', 'en_visita', 'rechazada']
  },
  
  CORREO_ENVIADO: {
    descripcion: 'Correo de bienvenida/visita enviado. SLA: 3 días hábiles para visitar',
    visible_en_home: false,
    puede_gestionar: false,
    proximo_paso: 'Agente marca inicio de visita en terreno',
    transiciones: ['en_visita', 'rechazada']
  },
  
  EN_VISITA: {
    descripcion: 'Agente programó visita de validación',
    visible_en_home: false,
    puede_gestionar: false,
    proximo_paso: 'Agente valida en terreno',
    transiciones: ['validada', 'rechazada']
  },
  
  VALIDADA: {
    descripcion: 'Agente validó empresa en terreno',
    visible_en_home: true,
    puede_gestionar: false,
    proximo_paso: 'Admin activa completamente',
    transiciones: ['activa', 'rechazada']
  },
  
  // FLUJO 3: OPERACIÓN
  ACTIVA: {
    descripcion: 'Empresa visible y operativa en el sistema',
    visible_en_home: true,
    puede_gestionar: true,
    proximo_paso: 'Operación normal',
    transiciones: ['suspendida', 'inactiva']
  },
  
  SUSPENDIDA: {
    descripcion: 'Temporalmente desactivada por admin',
    visible_en_home: false,
    puede_gestionar: false,
    proximo_paso: 'Reactivar o desactivar permanentemente',
    transiciones: ['activa', 'inactiva']
  },
  
  INACTIVA: {
    descripcion: 'Desactivada temporalmente',
    visible_en_home: false,
    puede_gestionar: false,
    proximo_paso: 'Puede reactivarse por admin',
    transiciones: ['activa', 'pendiente_validacion']
  },
  
  RECHAZADA: {
    descripcion: 'No cumple requisitos de validación',
    visible_en_home: false,
    puede_gestionar: false,
    proximo_paso: 'Puede reactivarse para nueva revisión',
    transiciones: ['pendiente_validacion', 'ingresada']
  }
};

// ========================================
// FLUJO DE SOLICITUDES NUEVAS
// ========================================

export const FLUJO_SOLICITUD = {
  PENDIENTE: {
    descripcion: 'Solicitud nueva de proveedor',
    proximo_paso: 'Revisión administrativa',
    transiciones: ['en_revision', 'rechazada']
  },
  
  EN_REVISION: {
    descripcion: 'Bajo revisión administrativa',
    proximo_paso: 'Aprobar para validación por agente',
    transiciones: ['aprobada', 'rechazada']
  },
  
  APROBADA: {
    descripcion: 'Aprobada para validación por agente',
    proximo_paso: 'Asignar a agente',
    transiciones: [] // Se convierte en empresa con estado 'pendiente_validacion'
  },
  
  RECHAZADA: {
    descripcion: 'Rechazada administrativamente',
    proximo_paso: 'Estado final',
    transiciones: []
  }
};

// ========================================
// FUNCIONES DE TRANSICIÓN
// ========================================

export const puedeTransicionar = (estadoActual, nuevoEstado, esSolicitud = false) => {
  const flujo = esSolicitud ? FLUJO_SOLICITUD : FLUJO_EMPRESA;
  const estados = esSolicitud ? ESTADOS_SOLICITUD : ESTADOS_EMPRESA;
  
  // Normalizar estadoActual: convertir valor string a constante si es necesario
  // Ejemplo: 'correo_enviado' -> 'CORREO_ENVIADO'
  let estadoKey = estadoActual;
  if (typeof estadoActual === 'string') {
    // Buscar la constante que tiene este valor
    const constanteEncontrada = Object.keys(estados).find(key => estados[key] === estadoActual);
    if (constanteEncontrada) {
      estadoKey = constanteEncontrada;
    } else {
      // Si no se encuentra, usar el valor directamente (puede ser que ya sea una constante)
      estadoKey = estadoActual;
    }
  }
  
  // Obtener el objeto de estado del flujo
  const estado = flujo[estadoKey];
  if (!estado || !estado.transiciones) {
    console.warn(`Estado no encontrado en flujo: ${estadoKey} (original: ${estadoActual})`);
    return false;
  }
  
  // Normalizar nuevoEstado: puede venir como constante o como valor
  // Las transiciones están definidas como valores (strings), no constantes
  // Ejemplo: transiciones: ['en_visita', 'rechazada']
  const nuevoEstadoValor = typeof nuevoEstado === 'string' && nuevoEstado in estados 
    ? estados[nuevoEstado] // Si es constante, obtener su valor
    : nuevoEstado; // Si ya es valor, usarlo directamente
  
  // Verificar si el nuevoEstado (como valor) está en las transiciones
  return estado.transiciones.includes(nuevoEstadoValor);
};

export const obtenerSiguientesEstados = (estadoActual, esSolicitud = false) => {
  const flujo = esSolicitud ? FLUJO_SOLICITUD : FLUJO_EMPRESA;
  return flujo[estadoActual]?.transiciones || [];
};

export const obtenerDescripcionEstado = (estado, esSolicitud = false) => {
  const flujo = esSolicitud ? FLUJO_SOLICITUD : FLUJO_EMPRESA;
  return flujo[estado]?.descripcion || 'Estado desconocido';
};

/**
 * Etiquetas únicas para mostrar estado en UI (Agente, Proveedor, Admin).
 * Una sola fuente de verdad para que "en_visita" sea siempre "En visita", etc.
 */
export const ESTADO_UI_LABELS = {
  [ESTADOS_EMPRESA.ACTIVA]: 'Activa',
  activada: 'Activa',
  aprobada: 'Activa',
  [ESTADOS_EMPRESA.INACTIVA]: 'Inactiva',
  [ESTADOS_EMPRESA.SUSPENDIDA]: 'Inactiva',
  rechazada: 'Rechazada',
  [ESTADOS_EMPRESA.EN_VISITA]: 'En Visita',
  en_visita: 'En Visita',
  [ESTADOS_EMPRESA.CORREO_ENVIADO]: 'Correo Enviado',
  correo_enviado: 'Correo Enviado',
  [ESTADOS_EMPRESA.PENDIENTE_VALIDACION]: 'Pendiente',
  pendiente_validacion: 'Pendiente',
  pendiente: 'Pendiente',
  [ESTADOS_EMPRESA.VALIDADA]: 'Validada',
  validada: 'Validada',
  asignada: 'Asignada',
  en_revision: 'En Revisión',
  ingresada: 'Ingresada',
  credenciales_asignadas: 'Credenciales Asignadas',
  catalogada: 'Catalogada',
};

/**
 * Devuelve la etiqueta de estado para mostrar en toda la app (Agente, Proveedor, Admin).
 * @param {string} estado - Valor de estado en BD (empresas o solicitudes_empresa)
 * @returns {string}
 */
export function obtenerEtiquetaEstadoParaUI(estado) {
  if (!estado || typeof estado !== 'string') return 'Pendiente';
  const normalizado = String(estado).toLowerCase().trim().replace(/\s/g, '_');
  return ESTADO_UI_LABELS[normalizado] || ESTADO_UI_LABELS[estado] || estado;
}

/**
 * True solo cuando la empresa/solicitud está operativa (visible y con acceso).
 * Usar para badge "Activa"/"Inactiva" en dashboard proveedor y consistencia con agente/admin.
 * NO usa visible_en_busqueda ni estado_validacion para evitar mostrar "Activa" cuando el flujo sigue en en_visita/correo_enviado.
 */
export function esEmpresaActiva(empresa) {
  if (!empresa) return false;
  const e = String(empresa.estado || '').toLowerCase().trim();
  const activos = ['activa', 'activada', 'aprobada'];
  if (activos.includes(e)) return true;
  if (empresa.tiene_credenciales_asignadas === true) return true;
  return false;
}

/** Empresa visible en directorio/HomePage. publicStatus !== 'INACTIVE' (por defecto ACTIVE). */
export function isEmpresaVisiblePublicamente(empresa) {
  if (!empresa) return false;
  const status = (empresa.publicStatus || '').toUpperCase();
  if (status === 'INACTIVE') return false;
  return true;
}

/**
 * Mismo criterio que HomePage al listar empresas públicas:
 * estado activa o validada y visible (publicStatus !== INACTIVE).
 * Ya no se exige tipoEmpresa === proveedor.
 */
export function empresaIncluidaEnListadoHome(empresa) {
  if (!empresa) return false;
  const estado = (empresa.estado || '').toLowerCase();
  if (estado !== ESTADOS_EMPRESA.ACTIVA && estado !== ESTADOS_EMPRESA.VALIDADA) return false;
  return isEmpresaVisiblePublicamente(empresa);
}

/**
 * Valor a persistir en Firestore para tipoEmpresa cuando la empresa debe contar en el directorio/Home.
 * - Vacío, pyme, empresa, etc. → 'proveedor' (listado público solo muestra proveedor).
 * - Concesionaria se mantiene (dashboard y queries específicas).
 * Usar al crear/activar empresa desde solicitudes, agente o admin para no volver a guardar vacío/pyme por error.
 */
export function normalizarTipoEmpresaAlGuardar(tipoEmpresa) {
  const t = String(tipoEmpresa || '').toLowerCase().trim();
  if (!t) return 'proveedor';
  if (t === 'concesionaria' || t.includes('concesion')) return 'concesionaria';
  return 'proveedor';
}

// ========================================
// CAMPOS ESTÁNDAR (SNAKE_CASE)
// ========================================

export const CAMPOS_EMPRESA = {
  // Información básica
  NOMBRE: 'nombre_empresa',
  EMAIL: 'email_empresa',
  TELEFONO: 'telefono_empresa', 
  RUT: 'rut_empresa',
  DIRECCION: 'direccion_empresa',
  WEB: 'web_empresa',
  COMUNA: 'comuna',
  REGION: 'region',
  CATEGORIA: 'categoria',
  DESCRIPCION: 'descripcion_empresa',
  
  // Información adicional
  ANOS_FUNCIONAMIENTO: 'anos_funcionamiento',
  LOGO_URL: 'logo_url',
  
  // Redes sociales
  REDES_SOCIALES: 'redes_sociales'
};

export const CAMPOS_REPRESENTANTE = {
  NOMBRES: 'nombres_representante',
  APELLIDOS: 'apellidos_representante', 
  EMAIL: 'email_representante',
  TELEFONO: 'telefono_representante',
  CARGO: 'cargo_representante'
};

export const CAMPOS_FECHA = {
  REGISTRO: 'fecha_registro',
  ACTIVACION: 'fecha_activacion',
  ACTUALIZACION: 'fecha_actualizacion', 
  SUSPENSION: 'fecha_suspension',
  RECHAZO: 'fecha_rechazo',
  VALIDACION: 'fecha_validacion',
  VISITA: 'fecha_visita'
};

// ========================================
// FUNCIONES DE NORMALIZACIÓN
// ========================================

/**
 * Normaliza datos de empresa a formato estándar
 * Maneja diferentes variantes de nombres de campos
 */
export const normalizeEmpresaData = (data) => {
  const normalized = {};
  
  // Información básica de empresa
  normalized[CAMPOS_EMPRESA.NOMBRE] = data.nombre_empresa || data.nombreEmpresa || data.nombre || '';
  normalized[CAMPOS_EMPRESA.EMAIL] = data.email_empresa || data.emailEmpresa || data.email || '';
  normalized[CAMPOS_EMPRESA.TELEFONO] = data.telefono_empresa || data.telefonoEmpresa || data.telefono || '';
  normalized[CAMPOS_EMPRESA.RUT] = data.rut_empresa || data.rutEmpresa || data.rut || '';
  normalized[CAMPOS_EMPRESA.DIRECCION] = data.direccion_empresa || data.direccionEmpresa || data.direccion || '';
  normalized[CAMPOS_EMPRESA.WEB] = data.web_empresa || data.web_actual || data.web || '';
  normalized[CAMPOS_EMPRESA.COMUNA] = data.comuna || '';
  normalized[CAMPOS_EMPRESA.REGION] = data.region || '';
  normalized[CAMPOS_EMPRESA.CATEGORIA] = data.categoria || 'Sin categoría';
  normalized[CAMPOS_EMPRESA.DESCRIPCION] = data.descripcion_empresa || data.descripcion || '';
  
  // Información adicional
  normalized[CAMPOS_EMPRESA.ANOS_FUNCIONAMIENTO] = data.anos_funcionamiento || '';
  normalized[CAMPOS_EMPRESA.LOGO_URL] = data.logo_url || '';
  normalized[CAMPOS_EMPRESA.REDES_SOCIALES] = data.redes_sociales || {
    facebook: '',
    instagram: '', 
    linkedin: '',
    twitter: ''
  };
  
  // Representante - normalizar diferentes variantes
  normalized.representante = {
    [CAMPOS_REPRESENTANTE.NOMBRES]: data.nombres_representante || data.representante_nombre || '',
    [CAMPOS_REPRESENTANTE.APELLIDOS]: data.apellidos_representante || data.representante_apellidos || '',
    [CAMPOS_REPRESENTANTE.EMAIL]: data.email_representante || data.representante_email || '',
    [CAMPOS_REPRESENTANTE.TELEFONO]: data.telefono_representante || data.representante_telefono || '',
    [CAMPOS_REPRESENTANTE.CARGO]: data.cargo_representante || data.representante_cargo || ''
  };
  
  // Fechas - normalizar diferentes variantes
  normalized[CAMPOS_FECHA.REGISTRO] = data.fecha_registro || data.fechaRegistro || data.fechaCreacion || new Date();
  normalized[CAMPOS_FECHA.ACTIVACION] = data.fecha_activacion || data.fechaActivacion || null;
  normalized[CAMPOS_FECHA.ACTUALIZACION] = data.fecha_actualizacion || data.fechaActualizacion || new Date();
  
  // Estado - normalizar a minúscula y usar estados unificados
  const estadoOriginal = data.estado || 'pendiente';
  normalized.estado = normalizarEstado(estadoOriginal);
  
  return normalized;
};

/**
 * Normaliza estado a formato unificado
 */
export const normalizarEstado = (estado) => {
  const estadoLower = estado.toLowerCase();
  
  // Mapeo de estados antiguos a nuevos
  const mapeoEstados = {
    'enviada': ESTADOS_EMPRESA.CATALOGADA,
    'enviado': ESTADOS_EMPRESA.CATALOGADA,
    'pendiente': ESTADOS_EMPRESA.PENDIENTE_VALIDACION,
    'en revisión': ESTADOS_EMPRESA.PENDIENTE_VALIDACION,
    'en_revision': ESTADOS_EMPRESA.PENDIENTE_VALIDACION,
    'correo enviado': ESTADOS_EMPRESA.CORREO_ENVIADO,
    'correo_enviado': ESTADOS_EMPRESA.CORREO_ENVIADO,
    'validada': ESTADOS_EMPRESA.VALIDADA,
    'validado': ESTADOS_EMPRESA.VALIDADA,
    'activa': ESTADOS_EMPRESA.ACTIVA,
    'activo': ESTADOS_EMPRESA.ACTIVA,
    'activada': ESTADOS_EMPRESA.ACTIVA,
    'suspendida': ESTADOS_EMPRESA.SUSPENDIDA,
    'inactiva': ESTADOS_EMPRESA.INACTIVA,
    'inactivo': ESTADOS_EMPRESA.INACTIVA,
    'rechazada': ESTADOS_EMPRESA.RECHAZADA,
    'rechazado': ESTADOS_EMPRESA.RECHAZADA
  };
  
  return mapeoEstados[estadoLower] || ESTADOS_EMPRESA.PENDIENTE_VALIDACION;
};

/**
 * Sanitiza un valor para que sea válido en Firestore (sin undefined ni entidades anidadas inválidas).
 * Firestore no acepta: undefined, Symbol, Function, File; sí acepta: null, string, number, boolean, Date, arrays y objetos planos.
 * Preserva sentinels de Firestore (serverTimestamp, etc.).
 * @param {*} value - Valor a sanitizar
 * @param {number} depth - Profundidad actual (límite 15 para evitar "invalid nested entity")
 */
export function sanitizeForFirestore(value, depth = 0) {
  const MAX_DEPTH = 15;
  if (depth > MAX_DEPTH) return null;
  if (value === undefined) return null;
  if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'function' || (typeof value === 'symbol')) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && value !== null && typeof (value).constructor !== 'undefined' && (value).constructor.name === 'Timestamp') return value;
  if (typeof value === 'object' && value !== null && '_methodName' in value) return value;
  if (typeof File !== 'undefined' && value instanceof File) return null;
  if (typeof Blob !== 'undefined' && value instanceof Blob) return null;
  if (Array.isArray(value)) return value.map((v) => sanitizeForFirestore(v, depth + 1)).filter((v) => v !== undefined);
  if (typeof value === 'object' && value !== null) {
    const out = {};
    for (const key of Object.keys(value)) {
      if (key.includes('.') || key === '') continue;
      try {
        const v = sanitizeForFirestore(value[key], depth + 1);
        if (v !== undefined) out[key] = v;
      } catch (_) {
        // omitir propiedades que fallen al leer (getters, etc.)
      }
    }
    return out;
  }
  return null;
}

/**
 * Sanitiza un objeto para Firestore y opcionalmente omite la clave datos_solicitud
 * para evitar "Property datos_solicitud contains an invalid nested entity".
 * @param {Object} obj - Objeto a sanitizar
 * @param {{ omitDatosSolicitud?: boolean }} opts - Si omitDatosSolicitud es true, no se escribe la clave datos_solicitud
 */
export function sanitizeForFirestoreWrite(obj, opts = {}) {
  const sanitized = sanitizeForFirestore(obj);
  if (sanitized && typeof sanitized === 'object' && !Array.isArray(sanitized) && opts.omitDatosSolicitud) {
    delete sanitized.datos_solicitud;
  }
  return sanitized;
}

/**
 * Normaliza datos de solicitud a formato estándar
 * Prioriza estructura de colección 'empresas' pero agrega campos de compatibilidad
 */
export const normalizeSolicitudData = (data) => {
  const normalized = normalizeEmpresaData(data);
  
  // Campos específicos de solicitud
  normalized.fecha_solicitud = data.fecha_solicitud || data.fechaSolicitud || new Date();
  normalized.tipo_solicitud = data.tipo_solicitud || 'empresa';
  normalized.agente_creador = data.agente_creador || null;
  normalized.requiere_visita = data.requiere_visita || false;
  
  // ESTRUCTURA PRINCIPAL: Seguir formato de colección 'empresas'
  // Campos principales que deben seguir el formato de 'empresas'
  normalized.nombre = normalized[CAMPOS_EMPRESA.NOMBRE] || '';
  normalized.email = normalized[CAMPOS_EMPRESA.EMAIL] || '';
  normalized.telefono = normalized[CAMPOS_EMPRESA.TELEFONO] || '';
  normalized.direccion = normalized[CAMPOS_EMPRESA.DIRECCION] || '';
  normalized.categoria = normalized[CAMPOS_EMPRESA.CATEGORIA] || 'Sin categoría';
  normalized.descripcion = normalized[CAMPOS_EMPRESA.DESCRIPCION] || '';
  normalized.web = normalized[CAMPOS_EMPRESA.WEB] || '';
  normalized.logo = normalized[CAMPOS_EMPRESA.LOGO_URL] || '';
  
  // Campos adicionales de estructura 'empresas'
  normalized.rubro = normalized.categoria; // rubro = categoria en empresas
  normalized.tipoEmpresa = normalizarTipoEmpresaAlGuardar(data.tipoEmpresa);
  normalized.destacado = data.destacado || false;
  normalized.fechaRegistro = normalized[CAMPOS_FECHA.REGISTRO];
  
  // Campos de redes sociales (formato empresas)
  normalized.instagram = data.instagram || data.redes_sociales?.instagram || '';
  normalized.whatsapp = data.whatsapp || data.telefono || normalized.telefono;
  
  // Campos adicionales que pueden venir de diferentes fuentes
  normalized.servicios = data.servicios || [];
  normalized.galeria = data.galeria || [];
  normalized.imagenLocal = data.imagenLocal || '';
  normalized.contactoAdicional = data.contactoAdicional || {};
  normalized.descripcionCompleta = data.descripcionCompleta || normalized.descripcion;
  
  // CAMPOS DE COMPATIBILIDAD CON ADMIN_ACTIVADOR
  // Estos campos se agregan para compatibilidad pero no prevalecen
  normalized.comuna = normalized[CAMPOS_EMPRESA.COMUNA] || '';
  normalized.region = normalized[CAMPOS_EMPRESA.REGION] || '';
  normalized.etapa_proceso = data.etapa_proceso || 'revision_inicial';
  normalized.fecha_activacion = data.fecha_activacion || null;
  normalized.logoAsignado = !!normalized.logo;
  normalized.webValidada = !!normalized.web;
  normalized.perfilCompleto = data.perfilCompleto || false;
  normalized.tiene_credenciales_asignadas = data.tiene_credenciales_asignadas || false;
  normalized.usuario_empresa = data.usuario_empresa || null;
  normalized.solicitud_origen_id = data.solicitud_origen_id || null;
  
  // Flags para índices/listados (matching y filtros por sugerencias)
  normalized.hasSuggestions = data.hasSuggestions === true;
  normalized.hasBestMatch = data.hasBestMatch === true;
  if (data.createdAt) normalized.createdAt = data.createdAt;
  
  // REPRESENTANTE: Mantener tanto estructura anidada como campos planos
  if (normalized.representante) {
    // Campos planos para compatibilidad con tabla admin (SOLO para compatibilidad)
    normalized.nombres_representante = normalized.representante[CAMPOS_REPRESENTANTE.NOMBRES] || '';
    normalized.apellidos_representante = normalized.representante[CAMPOS_REPRESENTANTE.APELLIDOS] || '';
    normalized.email_representante = normalized.representante[CAMPOS_REPRESENTANTE.EMAIL] || '';
    normalized.telefono_representante = normalized.representante[CAMPOS_REPRESENTANTE.TELEFONO] || '';
    normalized.cargo_representante = normalized.representante[CAMPOS_REPRESENTANTE.CARGO] || '';
  }
  
  // CAMPOS DE COMPATIBILIDAD CON TABLA ADMIN (no prevalecen en estructura final)
  normalized.web_actual = normalized.web; // Solo para compatibilidad con tabla
  normalized.nombre_empresa = normalized.nombre; // Solo para compatibilidad con tabla
  normalized.email_empresa = normalized.email; // Solo para compatibilidad con tabla
  normalized.telefono_empresa = normalized.telefono; // Solo para compatibilidad con tabla
  normalized.direccion_empresa = normalized.direccion; // Solo para compatibilidad con tabla
  normalized.descripcion_empresa = normalized.descripcion; // Solo para compatibilidad con tabla
  normalized.rut_empresa = normalized.rut || ''; // Solo para compatibilidad con tabla
  
  // Horarios estándar si no existen
  if (!normalized.horarios) {
    normalized.horarios = {
      lunes: { activo: true, inicio: '09:00', fin: '18:00' },
      martes: { activo: true, inicio: '09:00', fin: '18:00' },
      miercoles: { activo: true, inicio: '09:00', fin: '18:00' },
      jueves: { activo: true, inicio: '09:00', fin: '18:00' },
      viernes: { activo: true, inicio: '09:00', fin: '18:00' },
      sabado: { activo: false, inicio: '09:00', fin: '13:00' },
      domingo: { activo: false, inicio: '10:00', fin: '14:00' }
    };
  }
  
  return normalized;
};

/**
 * Valida que los datos de empresa tengan los campos requeridos
 */
export const validateEmpresaData = (data) => {
  const errors = [];
  
  // Campos obligatorios
  if (!data[CAMPOS_EMPRESA.NOMBRE]?.trim()) {
    errors.push('Nombre de empresa es obligatorio');
  }
  
  if (!data[CAMPOS_EMPRESA.EMAIL]?.trim()) {
    errors.push('Email de empresa es obligatorio');
  }
  
  if (!data[CAMPOS_EMPRESA.TELEFONO]?.trim()) {
    errors.push('Teléfono de empresa es obligatorio');
  }
  
  // Validar formato de email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (data[CAMPOS_EMPRESA.EMAIL] && !emailRegex.test(data[CAMPOS_EMPRESA.EMAIL])) {
    errors.push('Email de empresa no es válido');
  }
  
  // Validar representante si está presente
  if (data.representante) {
    if (!data.representante[CAMPOS_REPRESENTANTE.NOMBRES]?.trim()) {
      errors.push('Nombre del representante es obligatorio');
    }
    
    if (data.representante[CAMPOS_REPRESENTANTE.EMAIL] && 
        !emailRegex.test(data.representante[CAMPOS_REPRESENTANTE.EMAIL])) {
      errors.push('Email del representante no es válido');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Crea estructura estándar para nueva empresa
 * Sigue formato de colección 'empresas' con campos adicionales de admin_activador
 */
export const createEmpresaTemplate = (solicitudData = {}) => {
  const normalized = normalizeSolicitudData(solicitudData);
  
  return {
    // ESTRUCTURA PRINCIPAL: Formato colección 'empresas'
    nombre: normalized.nombre,
    email: normalized.email,
    telefono: normalized.telefono,
    direccion: normalized.direccion,
    categoria: normalized.categoria,
    rubro: normalized.categoria, // rubro = categoria en empresas
    descripcion: normalized.descripcion,
    descripcionCompleta: normalized.descripcionCompleta || normalized.descripcion,
    web: normalized.web,
    logo: normalized.logo,
    
    // Campos específicos de empresas
    tipoEmpresa: normalized.tipoEmpresa || 'proveedor',
    destacado: normalized.destacado || false,
    fechaRegistro: new Date(),
    servicios: normalized.servicios || [],
    galeria: normalized.galeria || [],
    imagenLocal: normalized.imagenLocal || '',
    instagram: normalized.instagram || '',
    whatsapp: normalized.whatsapp || normalized.telefono,
    contactoAdicional: normalized.contactoAdicional || {},
    
    // Estado y configuración (compatible con admin_activador)
    estado: ESTADOS_EMPRESA.ACTIVA,
    etapa_proceso: 'activada_sin_credenciales',
    fecha_activacion: new Date(),
    logoAsignado: !!normalized.logo,
    webValidada: !!normalized.web,
    perfilCompleto: false,
    tiene_credenciales_asignadas: false,
    usuario_empresa: null,
    
    // Campos adicionales de admin_activador
    comuna: normalized.comuna || '',
    region: normalized.region || '',
    solicitud_origen_id: normalized.id || null,
    
    // Representante (estructura anidada como en admin_activador)
    representante: normalized.representante || {
      [CAMPOS_REPRESENTANTE.NOMBRES]: '',
      [CAMPOS_REPRESENTANTE.APELLIDOS]: '',
      [CAMPOS_REPRESENTANTE.EMAIL]: '',
      [CAMPOS_REPRESENTANTE.TELEFONO]: '',
      [CAMPOS_REPRESENTANTE.CARGO]: ''
    },
    
    // Horarios estándar
    horarios: normalized.horarios || {
      lunes: { activo: true, inicio: '09:00', fin: '18:00' },
      martes: { activo: true, inicio: '09:00', fin: '18:00' },
      miercoles: { activo: true, inicio: '09:00', fin: '18:00' },
      jueves: { activo: true, inicio: '09:00', fin: '18:00' },
      viernes: { activo: true, inicio: '09:00', fin: '18:00' },
      sabado: { activo: false, inicio: '09:00', fin: '13:00' },
      domingo: { activo: false, inicio: '10:00', fin: '14:00' }
    }
  };
};

/**
 * Crea estructura estándar para nueva solicitud
 */
export const createSolicitudTemplate = (formData = {}, esAgente = false, agenteEmail = null) => {
  const normalized = normalizeSolicitudData(formData);
  
  return {
    ...normalized,
    
    // Estado y metadatos
    estado: ESTADOS_SOLICITUD.PENDIENTE,
    fecha_solicitud: new Date(),
    tipo_solicitud: esAgente ? 'agente' : 'proveedor',
    agente_creador: agenteEmail,
    
    // Configuraciones por defecto
    requiere_visita: !esAgente,
    prioridad: esAgente ? 'alta' : 'normal',
    documentacion_validada: false,
    
    // Campos específicos de agente
    ...(esAgente && {
      validacion_presencial_completada: formData.documentacion_validada || false,
      documento_empresa_verificado: formData.documento_empresa_verificado || false,
      firma_acuerdo_obtenida: formData.firma_acuerdo_obtenida || false,
      direccion_verificada_presencial: formData.direccion_verificada || false
    })
  };
};

// ========================================  
// UTILIDADES DE CONSULTA
// ========================================

/**
 * Obtiene consulta estándar para empresas activas
 */
export const getEmpresasActivasQuery = () => {
  return {
    collection: 'empresas',
    where: [
      ['estado', '==', ESTADOS_EMPRESA.ACTIVA]
    ],
    orderBy: [CAMPOS_FECHA.REGISTRO, 'desc'],
    limit: 50
  };
};

/**
 * Obtiene consulta estándar para solicitudes pendientes
 */
export const getSolicitudesPendientesQuery = () => {
  return {
    collection: 'solicitudes_empresa',
    where: [
      ['estado', 'in', [ESTADOS_SOLICITUD.PENDIENTE, ESTADOS_SOLICITUD.EN_REVISION]]
    ],
    orderBy: ['fecha_solicitud', 'desc']
  };
};

export default {
  ESTADOS_SOLICITUD,
  ESTADOS_EMPRESA,
  FLUJO_EMPRESA,
  FLUJO_SOLICITUD,
  ESTADO_UI_LABELS,
  obtenerEtiquetaEstadoParaUI,
  esEmpresaActiva,
  CAMPOS_EMPRESA,
  CAMPOS_REPRESENTANTE,
  CAMPOS_FECHA,
  puedeTransicionar,
  obtenerSiguientesEstados,
  obtenerDescripcionEstado,
  normalizarEstado,
  normalizeEmpresaData,
  normalizeSolicitudData,
  validateEmpresaData,
  createEmpresaTemplate,
  createSolicitudTemplate,
  getEmpresasActivasQuery,
  getSolicitudesPendientesQuery
};
